import React from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  Box,
  Autocomplete,
  TextField,
  Typography,
} from "@mui/material";
import theme from "theme";

import AddAddressesPopup from "pages/community/Addresses/AddAddressesPopup";
import { RectangularTextButton } from "components/Base/Buttons";

export type AddressType = {
  id: number;
  street_address?: string;
  city?: string;
  state?: string;
  post_code?: string;
  country?: string;
  [key: string]: any;
};

type Props = {
  open: boolean;
  title?: string;
  fetchAddresses?: () => Promise<AddressType[]>;
  addresses?: AddressType[];
  loading?: boolean;
  selected: AddressType[];
  onSelectedChange: (value: AddressType[]) => void;
  disabledIds?: number[];
  confirmLabel?: string;
  onClose: () => void;
  onConfirm: () => void;
  getOptionLabel?: (a: AddressType) => string;
};

const defaultLabel = (a: AddressType) =>
  `${a.street_address ?? ""} ${a.city ?? ""} ${a.post_code ?? ""}`.trim();

const uniqById = (items: AddressType[]) =>
  Array.from(new Map(items.filter(Boolean).map((a) => [a.id, a])).values());

const AddressPickerDialog: React.FC<Props> = ({
  open,
  title = "Choose Addresses",
  fetchAddresses,
  addresses,
  loading: loadingProp,
  selected,
  onSelectedChange,
  disabledIds = [],
  confirmLabel = "Save Addresses",
  onClose,
  onConfirm,
  getOptionLabel = defaultLabel,
}) => {
  const [allAddresses, setAllAddresses] = React.useState<AddressType[]>([]);
  const [loadingInternal, setLoadingInternal] = React.useState(false);
  const [addOpen, setAddOpen] = React.useState(false);

  const disabledSet = React.useMemo(() => new Set(disabledIds), [disabledIds]);
  const effectiveLoading = loadingProp ?? loadingInternal;

  const loadAddresses = React.useCallback(async () => {
    if (!fetchAddresses) return;

    setLoadingInternal(true);

    try {
      const res = await fetchAddresses();
      setAllAddresses(res || []);
    } catch (e) {
      console.error("Failed to load addresses:", e);
      setAllAddresses([]);
    } finally {
      setLoadingInternal(false);
    }
  }, [fetchAddresses]);

  React.useEffect(() => {
    if (Array.isArray(addresses)) setAllAddresses(addresses);
  }, [addresses]);

  React.useEffect(() => {
    if (!open) return;
    if (!fetchAddresses) return;

    let alive = true;

    (async () => {
      setLoadingInternal(true);
      
      try {
        const res = await fetchAddresses();
        if (alive) setAllAddresses(res || []);
      } catch (e) {
        console.error("Failed to load addresses:", e);
        if (alive) setAllAddresses([]);
      } finally {
        if (alive) setLoadingInternal(false);
      }
    })();

    return () => {
      alive = false;
    };
  }, [open, fetchAddresses]);

  const handleNewAddressSaved = async (newAddress: any) => {
    setAddOpen(false);

    if (fetchAddresses) {
      await loadAddresses();
    }

    if (newAddress?.id) {
      onSelectedChange(uniqById([...selected, newAddress]));
    }
  };

  return (
    <>
      <Dialog
        open={open}
        onClose={onClose}
        maxWidth="sm"
        fullWidth
        slotProps={{
          paper: {
            sx: {
              backgroundColor: theme.custom.base.background.default.dark,
              color: theme.custom.base.text.primary.dark,
            },
          },
        }}
      >
        <DialogTitle>{title}</DialogTitle>

        <DialogContent
          sx={{
            backgroundColor: theme.custom.base.background.default.dark,
            color: theme.custom.base.text.primary.dark,
          }}
        >
          <Box
            sx={{
              mt: 1,
              mb: 1,
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              gap: 1,
            }}
          >
            <Typography variant="body2" sx={{ opacity: 0.85 }}>
              Select one or more addresses, or add a new one.
            </Typography>

            <RectangularTextButton
              colour="clear"
              onClick={() => setAddOpen(true)}
            >
              Add New
            </RectangularTextButton>
          </Box>

          <Box sx={{ mt: 2 }}>
            <Autocomplete
              multiple
              loading={effectiveLoading}
              options={allAddresses}
              getOptionDisabled={(a) => disabledSet.has(a.id)}
              value={selected}
              onChange={(_, value) => onSelectedChange(value)}
              getOptionLabel={getOptionLabel}
              renderInput={(params) => (
                <TextField {...params} label="Search Addresses…" />
              )}
              sx={{
                backgroundColor: theme.custom.base.background.paper.dark,
                color: theme.custom.base.text.primary.dark,
              }}
            />
          </Box>

          <Box
            sx={{
              mt: 3,
              display: "flex",
              justifyContent: "flex-end",
              gap: 1,
            }}
          >
            <RectangularTextButton colour="clear" onClick={onClose}>
              Cancel
            </RectangularTextButton>

            <RectangularTextButton
              colour="highlighted"
              onClick={onConfirm}
              disabled={selected.length === 0}
            >
              {confirmLabel}
            </RectangularTextButton>
          </Box>
        </DialogContent>
      </Dialog>

      <AddAddressesPopup
        open={addOpen}
        onClose={() => setAddOpen(false)}
        onSaved={handleNewAddressSaved}
      />
    </>
  );
};

export default AddressPickerDialog;